import React from 'react';
import {Image, StyleSheet, View} from 'react-native';

import config from '../../config';
import logoImg from '../../images/brand/header-logo.png';

// SHORTCUT: layout literals with no token (the DIS-1859 redline's tokenGaps "535 / 164×28":
// "deliberate code deviation candidate: layout literals with no scale meaning"; a local
// DIS-1859 artifact, not a file in this repo). Upgrade path:
// layout.desktop header tokens once DIS-1735 adds them.
/** header/desk draws the logo in a 164 × 28 box; header/mob (65:3953) scales it to 26 tall. */
const LOGO_BOX_WIDTH = 164;
export const DESKTOP_LOGO_HEIGHT = 28;
export const MOBILE_LOGO_HEIGHT = 26;
// The header-logo.png files carry transparent padding, so sizing the PNG to the box drew the
// wordmark about 16 tall. Each brand instead scales its PNG so the visible ink is as tall as
// Bottlemart's (QA 2026-10-21: "all 4 brands the same logo size by height, reference BM"), and
// centres the ink on the box, as Figma does.
// `png` is the file size; `ink` is the opaque bounding box (alpha > 6%), both measured from
// assets/common/images/{brand}/header-logo.png.
// SHORTCUT: update when a brand's logo file changes.
export const LOGO_INK: Record<
  string,
  {png: [number, number]; ink: [number, number, number, number]}
> = {
  BM: {png: [2268, 608], ink: [86, 129, 2197, 470]},
  SNS: {png: [710, 190], ink: [33, 35, 676, 179]},
  HB: {png: [543, 121], ink: [49, 18, 499, 105]},
  TC: {png: [1692, 454], ink: [71, 68, 1645, 382]},
};
const inkWidth = ({ink}: {ink: number[]}) => ink[2] - ink[0] + 1;
const inkHeight = ({ink}: {ink: number[]}) => ink[3] - ink[1] + 1;
/** BM's ink is 159 wide at the 28 box (Figma header/desk 442:40761); its height is the reference. */
const BM_INK_WIDTH = 159;
export const LOGO_INK_HEIGHT = (BM_INK_WIDTH * inkHeight(LOGO_INK.BM)) / inkWidth(LOGO_INK.BM);
const logoInk = LOGO_INK[config.BASE_TENANT] ?? LOGO_INK.BM;

/**
 * The brand logo, ink-centred in a box `height` tall (164 × 28 at desktop). Decorative: the
 * pressable that wraps it carries the accessible name.
 */
export const HeaderLogo = ({height}: {height: number}) => {
  const box = height / DESKTOP_LOGO_HEIGHT;
  const width = LOGO_BOX_WIDTH * box;
  const scale = (LOGO_INK_HEIGHT * box) / inkHeight(logoInk);
  const image = {
    width: logoInk.png[0] * scale,
    height: logoInk.png[1] * scale,
    left: width / 2 - ((logoInk.ink[0] + logoInk.ink[2] + 1) / 2) * scale,
    top: height / 2 - ((logoInk.ink[1] + logoInk.ink[3] + 1) / 2) * scale,
  };
  return (
    <View style={{width, height}}>
      <Image
        source={logoImg}
        style={[styles.image, image]}
        resizeMode="contain"
        accessible={false}
      />
    </View>
  );
};

const styles = StyleSheet.create({image: {position: 'absolute'}});
